/**
 * Mobile Carousel Module
 * Activates below 700px on elements with [data-mobile-carousel]
 *
 * Two variants:
 *   data-mobile-carousel="peek"  — adjacent slides peek from edges, scroll-snap
 *   data-mobile-carousel="depth" — active slide foreground, adjacent recede/scale down
 *
 * Both: dot indicators, prev/next arrows, infinite wrapping, auto-advance
 */

const MOBILE_BP = 700;
const AUTO_DELAY = 5000; // ms between auto-advance

class MobileCarousel {
    constructor(el) {
        this.container = el;
        this.variant = el.dataset.mobileCarousel; // 'peek' or 'depth'
        this.slides = [...el.children];
        this.count = this.slides.length;
        this.current = 0;
        this.active = false;
        this._autoTimer = null;

        // Elements we inject
        this.wrapper = null;
        this.dotsWrap = null;
        this.dots = [];
        this.prevBtn = null;
        this.nextBtn = null;

        this._onResize = this._onResize.bind(this);
        window.addEventListener('resize', this._onResize);
        this._onResize();
    }

    _onResize() {
        const isMobile = window.innerWidth <= MOBILE_BP;
        if (isMobile && !this.active) this._activate();
        if (!isMobile && this.active) this._deactivate();
    }

    _activate() {
        this.active = true;
        this.current = 0;

        // Wrap the container in a carousel wrapper
        this.wrapper = document.createElement('div');
        this.wrapper.className = `carousel-wrapper carousel-wrapper--${this.variant}`;
        this.container.parentNode.insertBefore(this.wrapper, this.container);
        this.wrapper.appendChild(this.container);

        // Add carousel class to the slide container
        this.container.classList.add('carousel-track');

        // Mark slides
        this.slides.forEach((slide, i) => {
            slide.classList.add('carousel-slide');
            slide.dataset.carouselIndex = i;
        });

        // Create arrows
        this.prevBtn = document.createElement('button');
        this.prevBtn.className = 'carousel-arrow carousel-arrow--prev';
        this.prevBtn.setAttribute('aria-label', 'Previous slide');
        this.prevBtn.innerHTML = '<svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M15 4L7 12L15 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        this.nextBtn = document.createElement('button');
        this.nextBtn.className = 'carousel-arrow carousel-arrow--next';
        this.nextBtn.setAttribute('aria-label', 'Next slide');
        this.nextBtn.innerHTML = '<svg width="34" height="34" viewBox="0 0 24 24" fill="none"><path d="M9 4L17 12L9 20" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

        this.wrapper.appendChild(this.prevBtn);
        this.wrapper.appendChild(this.nextBtn);

        // Create dots
        this.dotsWrap = document.createElement('div');
        this.dotsWrap.className = 'carousel-dots';
        this.dots = this.slides.map((_, i) => {
            const dot = document.createElement('button');
            dot.className = 'carousel-dot';
            dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
            dot.addEventListener('click', () => {
                this.goTo(i);
                this._restartAuto();
            });
            this.dotsWrap.appendChild(dot);
            return dot;
        });
        this.wrapper.appendChild(this.dotsWrap);

        // Events — reset auto-timer on manual interaction
        this.prevBtn.addEventListener('click', () => {
            this.prev();
            this._restartAuto();
        });
        this.nextBtn.addEventListener('click', () => {
            this.next();
            this._restartAuto();
        });

        // Touch/swipe support — use bound methods so they can be removed on deactivate
        this._touchStartX = 0;
        this._touchEndX = 0;
        this._onTouchStart = (e) => {
            this._touchStartX = e.changedTouches[0].screenX;
        };
        this._onTouchEnd = (e) => {
            this._touchEndX = e.changedTouches[0].screenX;
            const diff = this._touchStartX - this._touchEndX;
            if (Math.abs(diff) > 40) {
                diff > 0 ? this.next() : this.prev();
                this._restartAuto();
            }
        };
        this.container.addEventListener('touchstart', this._onTouchStart, { passive: true });
        this.container.addEventListener('touchend', this._onTouchEnd, { passive: true });

        this._updateSlides();
        this._startAuto();
    }

    _deactivate() {
        this.active = false;
        this._stopAuto();

        // Remove resize listener to prevent leak
        window.removeEventListener('resize', this._onResize);

        // Remove touch handlers to prevent stacking on re-activate
        this.container.removeEventListener('touchstart', this._onTouchStart);
        this.container.removeEventListener('touchend', this._onTouchEnd);

        // Move container back out of wrapper
        this.wrapper.parentNode.insertBefore(this.container, this.wrapper);
        this.wrapper.remove();

        // Clean up classes and attributes
        this.container.classList.remove('carousel-track');
        this.slides.forEach(slide => {
            slide.classList.remove('carousel-slide', 'is-active', 'is-prev', 'is-next');
            slide.removeAttribute('data-carousel-index');
            slide.style.transform = '';
            slide.style.opacity = '';
        });

        this.wrapper = null;
        this.dotsWrap = null;
        this.dots = [];
        this.prevBtn = null;
        this.nextBtn = null;

        // Re-attach resize listener to detect when to reactivate
        window.addEventListener('resize', this._onResize);
    }

    _startAuto() {
        this._stopAuto();
        this._autoTimer = setInterval(() => this.next(), AUTO_DELAY);
    }

    _stopAuto() {
        if (this._autoTimer) {
            clearInterval(this._autoTimer);
            this._autoTimer = null;
        }
    }

    _restartAuto() {
        this._startAuto();
    }

    goTo(index) {
        this.current = ((index % this.count) + this.count) % this.count;
        this._updateSlides();
    }

    next() {
        this.goTo(this.current + 1);
    }

    prev() {
        this.goTo(this.current - 1);
    }

    _updateSlides() {
        const prevIndex = ((this.current - 1) + this.count) % this.count;
        const nextIndex = (this.current + 1) % this.count;

        this.slides.forEach((slide, i) => {
            slide.classList.remove('is-active', 'is-prev', 'is-next');

            if (i === this.current) {
                slide.classList.add('is-active');
            } else if (i === prevIndex) {
                slide.classList.add('is-prev');
            } else if (i === nextIndex) {
                slide.classList.add('is-next');
            }
        });

        // Update dots
        this.dots.forEach((dot, i) => {
            dot.classList.toggle('is-active', i === this.current);
        });
    }
}

export function initCarousels() {
    const els = document.querySelectorAll('[data-mobile-carousel]');
    if (!els.length) return;
    els.forEach(el => new MobileCarousel(el));
}
